import { useState } from 'react';
import UpsellTrigger from '@alltrails/amplitude/enums/UpsellTrigger';
import useUser from '@alltrails/context/hooks/useUser';
import type PurchaseTier from '@alltrails/core/types/PurchaseTier';
import BaseUpsellModal from '../BaseUpsell/BaseUpsellModal';
import getCtaText from '../getCtaText';
import GenericUpsellContent from '../UpsellContent/GenericUpsellContent';
import getDefaultTierSelected from './getDefaultTierSelected';

type Props = {
  forceTier?: PurchaseTier;
  heading: React.ReactNode;
  hidePlus?: boolean;
  isOpen: boolean;
  isFocusPaused?: boolean;
  onCloseRequest: () => void;
  onSuccess?: () => void;
  promoEligibleHeading: React.ReactNode;
  showPlusFirst?: boolean;
  skipCheckout?: boolean;
  subheading?: React.ReactNode;
  upsellTrigger: UpsellTrigger;
  isCrossPlatform?: boolean;
  onCrossPlatformRequest?: () => void;
};

const GenericUpsellModal = ({
  forceTier,
  heading,
  hidePlus = false,
  isFocusPaused = false,
  isOpen,
  onCloseRequest,
  onSuccess,
  promoEligibleHeading,
  showPlusFirst,
  skipCheckout = false,
  subheading,
  upsellTrigger,
  isCrossPlatform,
  onCrossPlatformRequest
}: Props) => {
  const user = useUser();
  const isPlus = user?.subscriptionTier === 'plus';
  const defaultTierSelected = forceTier ?? getDefaultTierSelected(isPlus);
  const [tierSelected, setTierSelected] = useState<PurchaseTier>(defaultTierSelected);

  return (
    <BaseUpsellModal
      heading={heading}
      isOpen={isOpen}
      isFocusPaused={isFocusPaused}
      onCloseRequest={onCloseRequest}
      onSuccess={onSuccess}
      primaryButtonText={getCtaText(tierSelected, user?.isPromoEligible)}
      promoEligibleHeading={promoEligibleHeading}
      tallerOnMobile
      upsellTrigger={upsellTrigger}
      purchaseTier={tierSelected}
      productsAvailable={forceTier ?? (isPlus ? 'peak' : 'plus-peak')}
      skipCheckout={skipCheckout}
      isCrossPlatform={isCrossPlatform}
      onCrossPlatformRequest={onCrossPlatformRequest}
    >
      <GenericUpsellContent
        heading={heading}
        hidePeak={forceTier === 'plus'}
        hidePlus={hidePlus && forceTier !== 'plus'}
        promoEligibleHeading={promoEligibleHeading}
        setTierSelected={setTierSelected}
        showPlusFirst={showPlusFirst}
        subscriptionUser={user}
        tierSelected={tierSelected}
        subheading={subheading}
        upsellTrigger={upsellTrigger}
      />
    </BaseUpsellModal>
  );
};

export default GenericUpsellModal;
